import { UUID } from '../../../../../types/shared/UUID';
import { clientUrls } from '../../../../../ui-toolkit/rest/ClientUrlFactory';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { IPCDashboardTaskData } from '../../../../../types/pcDashboard/IDashboardTasks';
import { usePatchTaskMutation } from '../../../../../API/order/OrderTaskAPI';
import { convertIOrderTaskDataToIUpdateOrderTaskRequest, IUpdateOrderTaskRequest } from '../../../../../types/tasks';
import { Anchor, Checkbox, Paragraph, ShowHideWordCount } from '@zillow/constellation';
import { useHasFeatureFlag } from '../../../../../selectors/useHasFeatureFlag';
import { SpruceOrderId } from '../../../util/spruceOrderIndicator';
import { useGetConfigQuery } from '../../../../../API/applicationConfig/ApplicationConfigAPI';

const NewSaveTaskCheckBox: React.FC<{
  task: IPCDashboardTaskData;
}> = ({ task }) => {
  const [patchTask] = usePatchTaskMutation();
  const completeTask = useCallback(
    async (taskId: UUID, orderId: UUID) => {
      const taskOrderRequest: IUpdateOrderTaskRequest = convertIOrderTaskDataToIUpdateOrderTaskRequest({
        id: taskId,
        completed: true,
      });
      await patchTask({ orderId: orderId, body: taskOrderRequest });
    },
    [patchTask]
  );

  return <Checkbox checked={task.completed} onChange={() => completeTask(task.taskId, task.orderId)} />;
};

export const useGetTaskColumns = (): IBootstrapTableColumnProps<IPCDashboardTaskData>[] => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const { data: config } = useGetConfigQuery();

  return [
    {
      dataField: 'taskId',
      formatter: (_, row) => <NewSaveTaskCheckBox task={row} />,
    },
    {
      dataField: 'fileNumber',
      text: 'Order ID',
      formatter: (_, row) => {
        const useSpruceLinkFromConfig = isZillowClosingEnabled && (row.isSpruce ?? false);
        const href =
          useSpruceLinkFromConfig && config?.spruceInstanceHostname
            ? clientUrls.urlFor.spruceOrderStatus(row.orderId, config.spruceInstanceHostname)
            : clientUrls.urlFor.orderStatus(row.orderId);

        return (
          <Anchor href={href} target={'_blank'} rel={'noreferrer noopener'}>
            <SpruceOrderId fileNumber={row.fileNumber} showIcon={useSpruceLinkFromConfig} />
          </Anchor>
        );
      },
    },
    {
      dataField: 'taskName',
      formatter: (_, row) => {
        return <ShowHideLimitTen row={row} />;
      },
      text: 'Task',
    },
    {
      dataField: 'dueDate',
      text: 'Due By',
      formatter: (_, row) => {
        if (!row.dueDate) {
          return '';
        }
        return DateTime.fromISO(row.dueDate).toLocaleString(DateTime.DATETIME_SHORT);
      },
      sort: true,
    },
  ];
};

const ShowHideLimitTen: React.FC<{ row: IPCDashboardTaskData }> = ({ row }) => {
  const wordCount = row.taskName.match(/\s+/g)?.length ?? 0;

  if (wordCount <= 10) {
    return <Paragraph>{row.taskName}</Paragraph>;
  }
  return (
    <Paragraph>
      <ShowHideWordCount text={row.taskName} wordCount={10} />
    </Paragraph>
  );
};
